// ResultView — terminal success state. Shows downtime, image diff, summary.

import { Box, Button, Group, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { HeroCardiograph } from './HeroCardiograph'
import { OperationLogsViewer } from './OperationLogsViewer'
import { PhaseTimeline } from './PhaseTimeline'
import { COLOR, MOTION, SURFACE } from './tokens'
import type { DockerOperation } from './types'

interface Props {
  operation: DockerOperation
  onClose: () => void
}

function durationText(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return '—'
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.floor((ms % 60_000) / 1000)
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

function shortSha(s: unknown): string {
  if (typeof s !== 'string') return '—'
  const m = /sha256:([0-9a-f]{12})/.exec(s)
  return m ? `sha256:${m[1]}` : s.slice(0, 32)
}

export function ResultView({ operation, onClose }: Props) {
  const downtime = durationText(operation.started_at, operation.ended_at)

  const summary = useMemo(() => {
    const inspecting = operation.phases.find(p => p.name === 'inspecting')
    const recreating = operation.phases.find(p => p.name === 'recreating')
    const pulling = operation.phases.find(p => p.name === 'pulling')

    return {
      imageBefore: inspecting?.metadata?.image,
      imageAfter: recreating?.metadata?.image ?? inspecting?.metadata?.image,
      pullingOutput: pulling?.output ?? '',
    }
  }, [operation])

  return (
    <motion.div
      key="result"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={MOTION.viewSwap}
    >
      <Stack gap="md">
        <Box
          p={16}
          style={{
            background: COLOR.brandGradient,
            color: '#fff',
            borderRadius: 8,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Group gap={12} align="center">
            <ThemeIcon
              size={36}
              radius="xl"
              variant="filled"
              color="green"
              style={{ background: 'rgba(255,255,255,0.18)' }}
            >
              <IconCheck size={20} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={700} size="lg" style={{ fontFamily: '"Barlow", sans-serif' }}>
                Hard restart concluído
              </Text>
              <Text size="xs" style={{ opacity: 0.85 }}>
                {operation.container_name.replace(/^\//, '')} · downtime {downtime}
              </Text>
            </Stack>
          </Group>
          <Box mt={12} style={{ marginTop: 12, opacity: 0.92 }}>
            <HeroCardiograph state="healthy" height={64} />
          </Box>
        </Box>

        <Box
          p={12}
          style={{
            background: SURFACE.card,
            border: `1px solid ${SURFACE.cardBorder}`,
            borderRadius: 8,
          }}
        >
          <PhaseTimeline operation={operation} />
        </Box>

        <Box
          p={12}
          style={{
            background: SURFACE.card,
            border: `1px solid ${SURFACE.cardBorder}`,
            borderRadius: 8,
          }}
        >
          <Stack gap={8}>
            <SummaryRow label="Container" value={operation.container_name.replace(/^\//, '')} mono />
            {summary.imageBefore != null && (
              <SummaryRow label="Imagem" value={shortSha(summary.imageAfter ?? summary.imageBefore)} mono />
            )}
            <SummaryRow label="Volumes limpos" value={operation.clean_volumes ? 'sim' : 'não'} />
            <SummaryRow label="Início" value={new Date(operation.started_at).toLocaleString('pt-BR')} />
            <SummaryRow label="Fim" value={operation.ended_at ? new Date(operation.ended_at).toLocaleString('pt-BR') : '—'} />
            <SummaryRow label="Duração total" value={downtime} mono />
          </Stack>
        </Box>

        <OperationLogsViewer operation={operation} defaultOpen={false} />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Fechar
          </Button>
        </Group>
      </Stack>
    </motion.div>
  )
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Group justify="space-between" align="baseline" wrap="nowrap">
      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text
        size="xs"
        style={{
          fontFamily: mono ? '"JetBrains Mono", monospace' : '"Barlow", sans-serif',
          color: SURFACE.textPrimary,
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Text>
    </Group>
  )
}
